/* eslint-disable react-hooks/exhaustive-deps */
import { isNullOrUndefined } from 'app/helpers/objectHelpers';
import { InputAdornment, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { usePromiseTracker } from 'react-promise-tracker';
import { useSelector } from 'react-redux';
import {
  selectApprovalStatusSelected,
  selectIsEditMod,
  selectStandard,
} from 'store/redux-store/placement-mods/selectors';
import { validDecimalAndLength } from '../../ModificationsUtils';

interface BillingAmountProps {
  readOnly?: boolean;
}

export const BillingAmount = ({ readOnly }: BillingAmountProps) => {
  const { control, formState: { errors }, getValues, setValue, clearErrors, setError } = useFormContext();
  const { t } = useTranslation();
  const billingAmount = 'billingAmount';
  const billingAmountWatch: number = useWatch({ name: billingAmount });
  const billingType: string = useWatch({ name: 'billingType' });
  const isModEdit = useSelector(selectIsEditMod);
  const standard = useSelector(selectStandard);
  const { promiseInProgress: isSaveModApiCalling } = usePromiseTracker({ area: 'save-mod-api', delay: 0 });
  const [isFieldReadonly, setFieldReadonly] = useState<boolean>(false);
  const isApprovalStatusSelected = useSelector(selectApprovalStatusSelected);
  const isFieldEditable = () => {
    if (readOnly !== undefined) {
      return readOnly;
    }
    if (standard) {
      return true;
    } else {
      if (isFieldReadonly) {
        return true;
      } else {
        return !isModEdit;
      }
    }
  };

  const handleChange = (value: string) => {
    const userInput = parseFloat(value);
    if ((billingType === 'Flat' || billingType === 'Hourly') && (!userInput || userInput <= 0)) {
      if (isApprovalStatusSelected && standard === false) {
        clearErrors(billingAmount);
      } else {
        setError(billingAmount, { type: 'Required' });
      }
    } else {
      setValue(billingAmount, value);
      clearErrors(billingAmount);
    }
  };

  useEffect(() => {
    if (billingType === 'None') {
      setValue(billingAmount, '0.00');
      clearErrors(billingAmount);
      setFieldReadonly(true);
    } else {
      setFieldReadonly(false);
      handleChange(getValues(billingAmount));
    }
  }, [billingType, isFieldReadonly]);
  return (
    <Controller
      control={control}
      name={billingAmount}
      rules={{
        required: true,
        min: isFieldReadonly ? 0 : 0.01,
      }}
      render={({ field: { onChange, value, ...rest } }) => (
        <TextField
          variant="filled"
          fullWidth
          id="billingAmount"
          color="primary"
          autoComplete="off"
          label={t('placement.placementMods.billingAmount')}
          InputLabelProps={{
            shrink: billingAmountWatch ? true : false,
          }}
          InputProps={{
            readOnly: isFieldEditable(),
            style: {
              pointerEvents: standard || !isModEdit ? 'none' : 'auto',
            },
            startAdornment: <>{value && <InputAdornment position="start">{t('currency.dollar')}</InputAdornment>}</>,
          }}
          value={getValues(billingAmount) ?? ''}
          onChange={e => {
            if (validDecimalAndLength(e.target.value, { numberLength: 6, decimalLength: 2 })) {
              const validInput = e.target.value.replace(/^\./, '0.');
              onChange(validInput);
              handleChange(validInput);
            }
          }}
          {...rest}
          disabled={isFieldReadonly || isSaveModApiCalling}
          error={!!errors?.billingAmount}
          helperText={
            !isNullOrUndefined(errors?.billingAmount) ? t('placement.placementMods.billAmountError') : undefined
          }
        />
      )}
    />
  );
};
